import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import {
  SubscriptionDetail,
  subscriptionFactory,
} from '@/types/subscriptions/subscription';
import { useAppSelector } from '@/store';
import { authStore } from '@/store/auth';
import { pages } from '@/settings/pages';
import { subscriptionRequestsApi } from '@/services/api';
import { useFlashMessenger } from '@/hooks/useFlashMessenger';

import { Detail } from '../Presentation/Detail';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';

export const SubscriptionDetailContainer: React.FC = () => {
  const { apiErrorHandler } = useApiErrorHandler();
  const { flashNotice } = useFlashMessenger();
  const organizationId = useAppSelector(
    authStore.selectors.organizationIdSelector,
  );

  const [isDataFetched, setIsDataFetched] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionDetail>(
    subscriptionFactory(),
  );

  const navigate = useNavigate();
  const { requestCode } = useParams<{ requestCode: string }>();
  const { t } = useTranslation();

  useEffect(() => {
    (async () => {
      try {
        const subscription = await subscriptionRequestsApi.get({
          data: {
            organizationId,
            requestCode,
          },
        });
        setSubscription(subscription);
        setIsDataFetched(true);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response !== undefined) {
          const isNotFound = err.response && err.response.status === 404;
          if (isNotFound) {
            navigate(pages.top);
          } else {
            apiErrorHandler(err);
          }
        }
      }
    })();
  }, [apiErrorHandler, navigate, organizationId, requestCode]);

  const cancelSubscription = async () => {
    try {
      await subscriptionRequestsApi.delete({
        data: {
          organizationId,
          requestCode,
        },
      });

      flashNotice(t('text.subscription.delete.done'));
      const service = subscription.services.pop();
      if (service && service.serviceGroup) {
        navigate(
          pages.service.detail.replace(
            ':id',
            service.serviceGroup.id.toString(),
          ),
        );
      } else {
        navigate(pages.top);
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response !== undefined) {
        const isNotFound = err.response && err.response.status === 404;
        if (isNotFound) {
          navigate(pages.top);
        } else {
          apiErrorHandler(err);
        }
      }
    }
  };

  const childProps = { isDataFetched, subscription, cancelSubscription };
  return <Detail {...childProps} />;
};
